import { z } from 'zod';

/**
 * Pay rule configuration. A rule template version holds one complete
 * RuleConfig; an employee assignment may override any subset of it.
 *
 * Hours tiers are cumulative per local day: with weekday tiers
 * [{upTo 7.6, ORD}, {upTo 9.6, OT1}, {upTo null, OT2}] the first 7.6 worked
 * hours in a day are ORD, the next 2 are OT1, the rest OT2.
 */
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM (24 hour)');

export const tierSchema = z.object({
  upToHours: z.number().positive().nullable(),
  category: z.string().min(1),
});
export type Tier = z.infer<typeof tierSchema>;

const tiersSchema = z
  .array(tierSchema)
  .min(1)
  .refine((tiers) => tiers.at(-1)?.upToHours === null, 'The last tier must be open-ended (upToHours: null)')
  .refine((tiers) => {
    let prev = 0;
    for (const t of tiers.slice(0, -1)) {
      if (t.upToHours === null || t.upToHours <= prev) return false;
      prev = t.upToHours;
    }
    return true;
  }, 'Tier limits must increase');

export const allowanceSchema = z.object({
  code: z.string().min(1),
  category: z.string().min(1),
  per: z.enum(['shift', 'day']),
  quantity: z.number().positive().default(1),
  minWorkedHours: z.number().nonnegative().nullable().default(null),
  siteKinds: z.array(z.string()).nullable().default(null),
  dayTypes: z.array(z.enum(['weekday', 'saturday', 'sunday', 'publicHoliday'])).nullable().default(null),
});
export type Allowance = z.infer<typeof allowanceSchema>;

export const ruleConfigSchema = z.object({
  ordinary: z.object({
    maxWeeklyHours: z.number().positive(),
    weeklyOvertimeCategory: z.string().nullable(),
    spanStart: hhmm.nullable(),
    spanEnd: hhmm.nullable(),
    outsideSpanCategory: z.string().nullable(),
    ordinaryCategory: z.string().default('ORD'),
  }),
  dayTiers: z.object({
    weekday: tiersSchema,
    saturday: tiersSchema,
    sunday: tiersSchema,
    publicHoliday: tiersSchema,
  }),
  publicHolidayMinimumHours: z.number().nonnegative().nullable(),
  breaks: z.object({
    restBreaksPaid: z.boolean(),
    minMealMinutes: z.number().int().nonnegative(),
    autoDeductAfterHours: z.number().positive().nullable(),
    autoDeductMinutes: z.number().int().nonnegative(),
    mealRequiredByHours: z.number().positive().nullable(),
  }),
  rounding: z.object({
    mode: z.enum(['none', 'nearest', 'employee']),
    incrementMinutes: z.union([z.literal(1), z.literal(5), z.literal(6), z.literal(15)]),
    applyTo: z.enum(['punch', 'shift']),
  }),
  grace: z.object({
    lateMinutes: z.number().int().nonnegative(),
    earlyMinutes: z.number().int().nonnegative(),
    earlyFinishMinutes: z.number().int().nonnegative(),
    earlyStartPolicy: z.enum(['paid', 'clip_to_roster']),
  }),
  minimumEngagementHours: z.number().positive().nullable(),
  allowances: z.array(allowanceSchema),
  shiftLimits: z.object({
    longShiftHours: z.number().positive(),
    autoClockOutHours: z.number().positive(),
  }),
  rosterPolicy: z.object({
    unrosteredWorkSeverity: z.enum(['blocking', 'review', 'off']),
    absentSeverity: z.enum(['review', 'info', 'off']),
  }),
});
export type RuleConfig = z.infer<typeof ruleConfigSchema>;

/** Deep-partial overrides stored on an employee assignment. */
export type RuleOverrides = DeepPartial<RuleConfig>;
type DeepPartial<T> = T extends (infer U)[]
  ? U[]
  : T extends object
    ? { [K in keyof T]?: DeepPartial<T[K]> }
    : T;

/**
 * Example values modelled on common Australian manufacturing award clauses.
 * They are a starting point only: Euro Marble or its payroll adviser must
 * confirm every value before pilot (spec section 7).
 */
export const EXAMPLE_FACTORY_FULL_TIME: RuleConfig = {
  ordinary: {
    maxWeeklyHours: 38,
    weeklyOvertimeCategory: 'OT1',
    spanStart: '06:00',
    spanEnd: '18:00',
    outsideSpanCategory: 'OT1',
    ordinaryCategory: 'ORD',
  },
  dayTiers: {
    weekday: [
      { upToHours: 7.6, category: 'ORD' },
      { upToHours: 9.6, category: 'OT1' },
      { upToHours: null, category: 'OT2' },
    ],
    saturday: [
      { upToHours: 2, category: 'OT1' },
      { upToHours: null, category: 'OT2' },
    ],
    sunday: [{ upToHours: null, category: 'OT2' }],
    publicHoliday: [{ upToHours: null, category: 'PH' }],
  },
  publicHolidayMinimumHours: 4,
  breaks: {
    restBreaksPaid: true,
    minMealMinutes: 30,
    autoDeductAfterHours: 5,
    autoDeductMinutes: 30,
    mealRequiredByHours: 5,
  },
  rounding: { mode: 'none', incrementMinutes: 1, applyTo: 'punch' },
  grace: { lateMinutes: 5, earlyMinutes: 15, earlyFinishMinutes: 5, earlyStartPolicy: 'clip_to_roster' },
  minimumEngagementHours: null,
  allowances: [
    {
      code: 'MEAL',
      category: 'ALLOW_MEAL',
      per: 'shift',
      quantity: 1,
      minWorkedHours: 9.6,
      siteKinds: null,
      dayTypes: null,
    },
  ],
  shiftLimits: { longShiftHours: 12, autoClockOutHours: 16 },
  rosterPolicy: { unrosteredWorkSeverity: 'blocking', absentSeverity: 'review' },
};

export const EXAMPLE_CASUAL_INSTALLER: RuleConfig = {
  ...EXAMPLE_FACTORY_FULL_TIME,
  ordinary: { ...EXAMPLE_FACTORY_FULL_TIME.ordinary, spanStart: '06:00', spanEnd: '19:00' },
  minimumEngagementHours: 3,
  allowances: [
    {
      code: 'TRAVEL',
      category: 'ALLOW_TRAVEL',
      per: 'day',
      quantity: 1,
      minWorkedHours: null,
      siteKinds: ['install'],
      dayTypes: null,
    },
  ],
  rosterPolicy: { unrosteredWorkSeverity: 'review', absentSeverity: 'info' },
};

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Deep merge where arrays and scalars in `over` replace those in `base`. */
export function mergeRules(base: RuleConfig, over: RuleOverrides | null | undefined): RuleConfig {
  if (!over) return base;
  const merge = (a: unknown, b: unknown): unknown => {
    if (b === undefined) return a;
    if (isPlainObject(a) && isPlainObject(b)) {
      const out: Record<string, unknown> = { ...a };
      for (const [k, v] of Object.entries(b)) out[k] = merge(a[k], v);
      return out;
    }
    return b;
  };
  return ruleConfigSchema.parse(merge(base, over));
}

/** One stretch of local dates during which a single resolved rule set applies. */
export interface RuleSegment {
  from: string; // YYYY-MM-DD inclusive
  to: string | null; // YYYY-MM-DD inclusive, null = open-ended
  ruleRef: string; // human-readable version label, stored on every line
  templateVersionId: string;
  assignmentId: string;
  config: RuleConfig;
}

export interface TemplateVersionInput {
  id: string;
  templateId: string;
  templateName: string;
  versionNumber: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  config: RuleConfig;
}
export interface AssignmentInput {
  id: string;
  templateId: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  overrides: RuleOverrides | null;
}

const maxDate = (a: string, b: string) => (a > b ? a : b);
const minDate = (a: string | null, b: string | null) => (a === null ? b : b === null ? a : a < b ? a : b);

/**
 * Intersects an employee's dated template assignments with each template's
 * dated versions, producing the segments the engine looks up by date.
 */
export function resolveRuleSegments(
  assignments: AssignmentInput[],
  versions: TemplateVersionInput[],
): RuleSegment[] {
  const segments: RuleSegment[] = [];
  for (const a of assignments) {
    for (const v of versions.filter((x) => x.templateId === a.templateId)) {
      const from = maxDate(a.effectiveFrom, v.effectiveFrom);
      const to = minDate(a.effectiveTo, v.effectiveTo);
      if (to !== null && to < from) continue;
      const hasOverrides = a.overrides && Object.keys(a.overrides).length > 0;
      segments.push({
        from,
        to,
        templateVersionId: v.id,
        assignmentId: a.id,
        ruleRef: `${v.templateName} v${v.versionNumber} (from ${v.effectiveFrom})${hasOverrides ? ' + overrides' : ''}`,
        config: mergeRules(v.config, a.overrides),
      });
    }
  }
  return segments.sort((x, y) => (x.from < y.from ? -1 : 1));
}

export function segmentFor(segments: RuleSegment[], localDate: string): RuleSegment | null {
  for (const s of segments) if (s.from <= localDate && (s.to === null || localDate <= s.to)) return s;
  return null;
}

/** True when two inclusive date ranges overlap (null end = open). */
export function rangesOverlap(aFrom: string, aTo: string | null, bFrom: string, bTo: string | null): boolean {
  return (aTo === null || bFrom <= aTo) && (bTo === null || aFrom <= bTo);
}
